import React from 'react';
import {ViewIdentifiable} from "../../../models/common/ViewSettings";
import {ActiveDataFilter} from "../../../models/common/DataFilter";
import { Chip, Theme } from '@mui/material';
import {removeDataFilter} from "../../../actions/common/UserViewSettingsActions";
import {makeStyles, useTheme} from "@mui/styles";
import {buttonStyles} from "../../../config/Theme";

export type DataFilterRemove = ( viewIdentifier: string, filter: ActiveDataFilter<any> ) => void

export interface ActiveDataFilterItemProps extends ViewIdentifiable {
    activeFilter: ActiveDataFilter<any>
    onRemoveFilter: typeof removeDataFilter | DataFilterRemove
    notRemovable?: boolean
}

const useStyle = makeStyles( ( theme: Theme ) => ({
    ...buttonStyles(theme),
    root: {
        padding: theme.spacing(),
        height: '32px'
    },
    chip: {
        backgroundColor: theme.palette.secondary.light,
        color: 'black',
        borderRadius: 8+"px",
        padding: "1rem",
        height: "42px",
        border: '1px solid rgba(0, 0, 0, 0.23)',
        fontWeight: 'bold',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.mode === 'dark' ? 'white' : 'black'
        }
    },
    chipRemoveIcon: {
        color: theme.palette.primary.dark + ' !important',
    }
}));

/**
 * Represents a UI component responsible for displaying and managing an active data filter in a designated view.
 * Used to allow users to visually see filters applied and optionally remove them based on their remove permissions.
 *
 * This function handles rendering either a removable or non-removable chip for the applied filter
 * and integrates the UI interaction for filter removal through a callback.
 *
 * @param viewIdentifier - A unique string identifier for the current view where the filter is applied.
 * @param activeFilter - An object containing details of the active filter, including its display name.
 * @param onRemoveFilter - A callback function triggered when the user removes the filter. This function receives the view identifier and the active filter details as arguments.
 * @param notRemovable - A boolean indicating whether the filter is non-removable by users (true if not removable).
 *
 * @return A JSX element rendering the styled chip for the active filter. The chip is displayed without a delete button if the filter is not removable.
 * @throws None explicitly, but ensure to handle any potential UI rendering or callback-related issues.
 */
export function ActiveDataFilterItem(
    {
        viewIdentifier,
        activeFilter,
        onRemoveFilter,
        notRemovable
    }: ActiveDataFilterItemProps
) {
    const classes = useStyle();
    const theme = useTheme();

    const onRemoveFilterClickHandler = ( _: React.MouseEvent<HTMLButtonElement, MouseEvent> ) => onRemoveFilter( viewIdentifier, activeFilter );

    if( notRemovable ) {
        return (
            <div className={ classes.root }>
                <Chip
                    label={activeFilter.displayName}
                    className={classes.chip}
                />
            </div>
        );
    } else {
        return (
            <div className={ classes.root }>
                <Chip
                    label={activeFilter.displayName}
                    clickable
                    classes={{ "deleteIcon": ( theme.palette.mode === 'dark'
                        ? classes.chipRemoveIcon
                        : classes.tableButtonStyle
                        )
                }}
                    onDelete={onRemoveFilterClickHandler}
                    className={classes.chip}
                />
            </div>
        );
    }

}